import _ from "lodash";
import { z } from "zod";
import {
  FormulaCoercion,
  type ListModel,
  type Model,
  type ModelFieldType,
  type ModelResult,
  ModelResultSchema,
  ModelSchema,
  type ObjectModel,
  compileModelResultZodSchema,
  compileModelZodSchema,
  getChildFields,
  getListModelPaths,
  getModelAtPath,
  isModelEmpty,
  mapModel,
} from "./model";
import {
  RPAEdgeSchema,
  RPANodeSchema,
  RPASessionStatus,
  RPASessionStatusSchema,
} from "./rpa";
import { type TemplatedText, TiptapDocSchema, type TiptapNode } from "./text";
import type { TiptapDoc } from "./text";

export type { Model, ObjectModel, ListModel, ModelFieldType, ModelResult };
export {
  mapModel,
  ModelSchema,
  ModelResultSchema,
  FormulaCoercion,
  compileModelResultZodSchema,
  compileModelZodSchema,
  getChildFields,
  getListModelPaths,
  getModelAtPath,
  isModelEmpty,
};

/**
 * Primitives shared across the workflow
 */
export enum ResultStatus {
  WAITING = "waiting",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum RunStatus {
  /**
   * No processing has started
   */
  PENDING = "pending",
  /**
   * Processing has started
   */
  PROCESSING = "processing",
  /**
   * Processing is complete, waiting for user steps
   */
  REVIEW = "review",
  /**
   * All steps completed
   */
  COMPLETED = "completed",
  /**
   * Processing has failed for some reason
   */
  FAILED = "failed",
}

/**
 * Base node
 * */

// Node is a base class for all entities, transforms, actions, etc. in the workflow
// Nodes can be connected to each other based on their dependencies (inputIds)
const BaseNodeDataSchema = z.object({
  /**
   * The id of the node. Ids are stable across workflow versions. Should be the primary way to refer to a node.
   */
  id: z.string(),
  name: z.string(),
  dependencyIds: z.array(z.string()).default([]),
});

// A result is a node that has been run
export const BaseResultSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  status: z.enum([
    ResultStatus.WAITING,
    ResultStatus.RUNNING,
    ResultStatus.COMPLETED,
    ResultStatus.FAILED,
  ]),
});

/**
 * Entities
 * */
export enum DocumentType {
  EMAIL = 'email-json',
  CSV = 'csv',
  XLSX = 'xlsx',
  PDF = 'pdf',
  JSON = 'json',
  TXT = 'txt',
}

export const DocumentTypeSchema = z.enum([
  DocumentType.CSV,
  DocumentType.EMAIL,
  DocumentType.PDF,
  DocumentType.XLSX,
  DocumentType.JSON,
  DocumentType.TXT,
]);

/**
 * Infer the document type from a string
 * @param str - The string to infer the document type from.
 * Can be a file extension, mime type, or a full file name
 * @returns The document type
 */
export const inferDocumentType = (str: string): DocumentType => {
  if (str.endsWith("pdf") || str === "application/pdf") {
    return DocumentType.PDF;
  }

  if (
    str.endsWith("xlsx") ||
    str ===
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return DocumentType.XLSX;
  }

  if (str.endsWith("csv")) {
    return DocumentType.CSV;
  }

  if (str.endsWith("json")) {
    return DocumentType.JSON;
  }

  if (str.endsWith("txt")) {
    return DocumentType.TXT;
  }

  throw new Error(`Unknown document type: ${str}`);
};

/**
 * Convert a document type to a mime type
 * @param documentType - The document type to convert
 * @returns The mime type
 */
export const documentTypeToMimeType = (documentType: DocumentType): string => {
  switch (documentType) {
    case DocumentType.PDF:
      return "application/pdf";
    case DocumentType.XLSX:
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case DocumentType.CSV:
      return "text/csv";
    case DocumentType.JSON:
      return "application/json";
    case DocumentType.TXT:
      return "text/plain";
    default:
      throw new Error(`Unknown document type: ${documentType}`);
  }
};

export const DocumentSchema = BaseNodeDataSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("document"),
  documentType: DocumentTypeSchema,
  exampleDocumentId: z.string().optional().nullable(),
  required: z.boolean().default(true),
  acceptMultiple: z.boolean().default(false),
  skipIngestion: z.boolean().default(false),

  // Use the same primitives as the TransformNode
  // Each entity gets a guaranteed extraction model
  model: ModelSchema,
  promptId: z.string().optional().nullable(),
});

export const EmailSchema = BaseNodeDataSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("email"),
  model: ModelSchema.optional(),
  toUsername: z.string(),
  acceptFrom: z.array(z.string()).default([]),
  replyAll: z.boolean().default(true),
  enabled: z.boolean().default(true),
  required: z.boolean().default(false),
});

export const APITriggerSchema = BaseNodeDataSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("api-trigger"),
  enabled: z.boolean().default(true),
});

export const EntitySchema = z.discriminatedUnion("subType", [
  DocumentSchema,
  EmailSchema,
  APITriggerSchema,
]);

export const DocumentResultSchema = BaseResultSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("document"),
  documentId: z.string().optional(),
  ...ModelResultSchema.shape,
});

export const EmailResultSchema = BaseResultSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("email"),
  documentId: z.string().optional(),
  messageId: z.string().optional(),
  content: z
    .object({
      from: z.string(),
      to: z.array(
        z.object({
          email: z.string(),
          name: z.string(),
        }),
      ),
      bcc: z.array(
        z.object({
          email: z.string(),
          name: z.string(),
        }),
      ),
      cc: z.array(
        z.object({
          email: z.string(),
          name: z.string(),
        }),
      ),
      subject: z.string(),
      replyTo: z.string().optional(),
      textBody: z.string().optional(),
      htmlBody: z.string().optional(),
      attachments: z
        .array(
          z.object({
            name: z.string(),
            contentType: z.string().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  ...ModelResultSchema.shape,
});

export const APITriggerResultSchema = BaseResultSchema.extend({
  type: z.literal("entity"),
  subType: z.literal("api-trigger"),
  // requestMetadata: z.any(), TODO: handle storing custom context customers pass in the API request
  ...ModelResultSchema.shape,
});

export const EntityResultSchema = z.discriminatedUnion("subType", [
  DocumentResultSchema,
  EmailResultSchema,
  APITriggerResultSchema,
]);

export const ExtractionSchema = BaseNodeDataSchema.extend({
  type: z.literal("transform"),
  subType: z.literal("extraction"),
  model: ModelSchema,
  promptId: z.string().optional().nullable(),
  showDescription: z.boolean().optional().default(false),
});

export const TransformSchema = z.discriminatedUnion("subType", [
  // Currently Extraction is the only transform
  // In the future, we could have other transforms like
  // Code Runner, etc.
  ExtractionSchema,
]);

export const ExtractionResultSchema = BaseResultSchema.extend({
  type: z.literal("transform"),
  subType: z.literal("extraction"),
  ...ModelResultSchema.shape,
});

export const TransformResultSchema = z.discriminatedUnion("subType", [
  ExtractionResultSchema,
]);

export const BaseActionNodeDataSchema = BaseNodeDataSchema.extend({
  /**
   * If automatic, the node will run automatically and save the result to S3
   * If manual, the node will need to be run manually
   **/
  runMode: z.enum(["automatic", "manual"]).default("manual"),
});

const SortDefinitionSchema = z.object({
  field: z.string(),
  method: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("standard"),
      direction: z.enum(["asc", "desc"]),
    }),
    z.object({
      type: z.literal("value-order"),
      order: z.array(z.string()),
    }),
  ]),
});

export type SortDefinition = z.infer<typeof SortDefinitionSchema>;

export const CSVExportConfigSchema = z.object({
  format: z.literal("csv"),
  fields: z.array(z.string()),
  orderBy: z.array(SortDefinitionSchema).optional(),
});

export const PDFExportConfigSchema = z.object({
  format: z.literal("pdf"),
  fields: z.array(z.string()),
  groupBy: z.string().optional(),
  orderBy: z.array(SortDefinitionSchema).optional(),
});

/** Excel Export Config */
const CellSchema = z.object({
  row: z.number().int(),
  column: z.number().int(),
});

const CellFieldSchema = z.object({
  cell: CellSchema,
  modelPath: z.string(),
});

const ListSchema = z.object({
  /** Column lists are duplicated to the right, row lists are duplicated downwards */
  direction: z.enum(["column", "row"]),
  modelName: z.string(),
  fields: z.array(CellFieldSchema),
});

export const TemplatedExcelSheetSchema = z.object({
  cells: z.array(CellFieldSchema),
  lists: z.array(ListSchema),
});

const TemplatedExcelFileSchema = z.object({
  sheets: z.array(
    z.object({
      name: z.string(),
      enabled: z.boolean().default(true),
      sheet: TemplatedExcelSheetSchema,
    }),
  ),
});

export type Cell = z.infer<typeof CellSchema>;
export type CellField = z.infer<typeof CellFieldSchema>;
export type List = z.infer<typeof ListSchema>;
export type TemplatedExcelSheet = z.infer<typeof TemplatedExcelSheetSchema>;
export type TemplatedExcelFile = z.infer<typeof TemplatedExcelFileSchema>;

export const ExcelExportConfigSchema = z.object({
  format: z.literal("excel"),
  templateDocumentId: z.string().optional(),
  editTemplate: TemplatedExcelFileSchema,
});

export enum ExportFileType {
  CSV = 'csv',
  PDF = 'pdf',
  XLSX = 'xlsx',
}

export const ExportActionSchema = BaseActionNodeDataSchema.extend({
  type: z.literal("action"),
  subType: z.literal("export"),
  // This action will export everything this node depends on
  // based on the provided configuration
  config: z.discriminatedUnion("format", [
    CSVExportConfigSchema,
    PDFExportConfigSchema,
    ExcelExportConfigSchema,
  ]),
  savedDocumentId: z.string().optional(),
});

export const SendEmailActionSchema = BaseActionNodeDataSchema.extend({
  type: z.literal("action"),
  subType: z.literal("send-email"),
  config: z.object({
    body: z.string(),
  }),
});

export const ExportActionResultSchema = BaseResultSchema.extend({
  type: z.literal("action"),
  subType: z.literal("export"),
  data: z
    .object({
      base64: z.string().optional(),
      fileType: z.enum([ExportFileType.CSV, ExportFileType.PDF, ExportFileType.XLSX]).optional(),
    })
    .optional()
    .nullable(),
  savedDocumentId: z.string().optional(),
});

export const SendEmailActionResultSchema = BaseResultSchema.extend({
  type: z.literal("action"),
  subType: z.literal("send-email"),
  data: z
    .object({
      messageId: z.string().optional(),
      error: z.string().optional(),
    })
    .optional(),
});

export const RPAActionSchema = BaseActionNodeDataSchema.extend({
  type: z.literal("action"),
  subType: z.literal("rpa"),
  nodes: z.array(RPANodeSchema).default([]),
  edges: z.array(RPAEdgeSchema).default([]),
});
export { RPANodeSchema, RPAEdgeSchema };

export const RESTActionSchema = BaseActionNodeDataSchema.extend({
  type: z.literal("action"),
  subType: z.literal("rest"),
  url: z.string(),
  method: z.enum(["GET", "POST", "PUT", "DELETE"]),
  headers: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
    }),
  ),
  body: z.string(),
});

export const RPAHistoryStepSchema = z.object({
  step: z.object({
    id: z.string(),
    data: z.object({
      actionType: z.enum(["Navigate", "Click", "Type", "Scroll", "Extract"]),
      prompt: z.string(),
    }),
  }),
  status: z.enum(["completed", "failed", "in_progress", "unstarted"]),
  started_at_ts: z.number().optional(),
  completed_at_ts: z.number().optional(),
  current_action: z.string().optional(),
});

export const RPAHistorySchema = z.object({
  timeline: z.array(RPAHistoryStepSchema),
});

export type RPAHistory = z.infer<typeof RPAHistorySchema>;
export type RPAHistoryStep = z.infer<typeof RPAHistoryStepSchema>;

export const RPAActionResultSchema = BaseResultSchema.extend({
  type: z.literal("action"),
  subType: z.literal("rpa"),
  data: ModelResultSchema.shape.data, // (?) thinking we'll store RPA outputs like this ??
  session: z
    .object({
      url: z.string(),
      debugUrl: z.string(),
      status: RPASessionStatusSchema.default(RPASessionStatus.NOT_RUN),
    })
    .optional(),
  history: RPAHistorySchema.optional(),
});

export const RESTActionResultSchema = BaseResultSchema.extend({
  type: z.literal("action"),
  subType: z.literal("rest"),
  data: z.any().optional(),
});

export const ActionSchema = z.discriminatedUnion("subType", [
  ExportActionSchema,
  RPAActionSchema,
  RESTActionSchema,
  SendEmailActionSchema,
]);

export const ActionResultSchema = z.discriminatedUnion("subType", [
  ExportActionResultSchema,
  RPAActionResultSchema,
  RESTActionResultSchema,
  SendEmailActionResultSchema,
]);

export const ResultOutputSchema = z.union([
  EntityResultSchema,
  TransformResultSchema,
  ActionResultSchema,
]);

// Groups of nodes that can be referred to by other nodes or
// elsewhere in the workflow.
export const GroupNodeSchema = BaseNodeDataSchema.extend({
  type: z.literal("group"),
  subType: z.literal("v1"),
  name: z.string(),
  // The children of this group are inside `BaseNodeDataSchema.dependencyIds`
});

export const NodeDataSchema = z.union([
  EntitySchema,
  TransformSchema,
  ActionSchema,
  GroupNodeSchema,
]);

export const WorkflowSchema = z.object({
  id: z.string(),
  name: z.string(),
  versionId: z.string(),
  nodes: z.array(NodeDataSchema),
});

export const RunSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  workflowVersionId: z.string(),
  status: z.enum([
    RunStatus.PENDING,
    RunStatus.PROCESSING,
    RunStatus.REVIEW,
    RunStatus.COMPLETED,
    RunStatus.FAILED,
  ]),
  nodes: z.array(NodeDataSchema),
  results: z.array(ResultOutputSchema),
});

export type Workflow = z.infer<typeof WorkflowSchema>;
export type Run = z.infer<typeof RunSchema>;
export type NodeData = z.infer<typeof NodeDataSchema>;
export type ResultOutput = z.infer<typeof ResultOutputSchema>;

export type GroupNode = z.infer<typeof GroupNodeSchema>;

export type EntityNode = z.infer<typeof EntitySchema>;
export type EntityNodeSubtype = EntityNode["subType"];
export type DocumentNode = z.infer<typeof DocumentSchema>;
export type EmailNode = z.infer<typeof EmailSchema>;
export type APITriggerNode = z.infer<typeof APITriggerSchema>;

export type EntityResult = z.infer<typeof EntityResultSchema>;
export type DocumentResult = z.infer<typeof DocumentResultSchema>;
export type EmailResult = z.infer<typeof EmailResultSchema>;
export type APITriggerResult = z.infer<typeof APITriggerResultSchema>;

export type TransformNode = z.infer<typeof TransformSchema>;
export type ExtractionNode = z.infer<typeof ExtractionSchema>;

export type TransformResult = z.infer<typeof TransformResultSchema>;
export type ExtractionResult = z.infer<typeof ExtractionResultSchema>;

export type ActionNode = z.infer<typeof ActionSchema>;
export type ActionSubtype = ActionNode["subType"];
export type ActionRunMode = ActionNode["runMode"];
export type ExportActionNode = z.infer<typeof ExportActionSchema>;

export type ExportCSVConfig = z.infer<typeof CSVExportConfigSchema>;
export type ExportPDFConfig = z.infer<typeof PDFExportConfigSchema>;
export type ExportExcelConfig = z.infer<typeof ExcelExportConfigSchema>;

export type ExportActionFileType = ExportActionNode["config"]["format"];
export type RPAActionNode = z.infer<typeof RPAActionSchema>;
export type RESTActionNode = z.infer<typeof RESTActionSchema>;

export type ActionResult = z.infer<typeof ActionResultSchema>;
export type ExportActionResult = z.infer<typeof ExportActionResultSchema>;
export type RPAActionResult = z.infer<typeof RPAActionResultSchema>;
export type RESTActionResult = z.infer<typeof RESTActionResultSchema>;

export type SendEmailActionNode = z.infer<typeof SendEmailActionSchema>;
export type SendEmailActionResult = z.infer<typeof SendEmailActionResultSchema>;

export const isEntityNode = (node: NodeData): node is EntityNode =>
  node.type === "entity";
export const isDocumentNode = (node: NodeData): node is DocumentNode =>
  node.type === "entity" && node.subType === "document";
export const isEmailNode = (node: NodeData): node is EmailNode =>
  node.type === "entity" && node.subType === "email";
export const isAPITriggerNode = (node: NodeData): node is APITriggerNode =>
  node.type === "entity" && node.subType === "api-trigger";
export const isExtractionNode = (node: NodeData): node is ExtractionNode =>
  node.type === "transform" && node.subType === "extraction";
export const isTransformNode = (node: NodeData): node is TransformNode =>
  node.type === "transform";
export const isActionNode = (node: NodeData): node is ActionNode =>
  node.type === "action";
export const isExportNode = (node: NodeData): node is ExportActionNode =>
  node.type === "action" && node.subType === "export";
export const isGroupNode = (node: NodeData): node is GroupNode =>
  node.type === "group";
export const isSendEmailActionNode = (
  node: NodeData,
): node is SendEmailActionNode =>
  node.type === "action" && node.subType === "send-email";
export const isRPAActionNode = (node: NodeData): node is RPAActionNode =>
  node.type === "action" && node.subType === "rpa";
export const isRESTActionNode = (node: NodeData): node is RESTActionNode =>
  node.type === "action" && node.subType === "rest";

export const isTransformResult = (
  result: ResultOutput,
): result is TransformResult => result.type === "transform";
export const isExtractionResult = (
  result: ResultOutput,
): result is ExtractionResult =>
  result.type === "transform" && result.subType === "extraction";
export const isDocumentResult = (
  result: ResultOutput,
): result is DocumentResult =>
  result.type === "entity" && result.subType === "document";
export const isEmailResult = (result: ResultOutput): result is EmailResult =>
  result.type === "entity" && result.subType === "email";
export const isAPITriggerResult = (
  result: ResultOutput,
): result is APITriggerResult =>
  result.type === "entity" && result.subType === "api-trigger";
export const isExportResult = (
  result: ResultOutput,
): result is ExportActionResult =>
  result.type === "action" && result.subType === "export";
export const isRPAActionResult = (
  result: ResultOutput,
): result is RPAActionResult =>
  result.type === "action" && result.subType === "rpa";

export const INTERNAL_RUN_MODEL_NAME = "__INTERNAL__ RunModel";

export const aggregateModelDefinitions = (nodes: NodeData[]) => {
  const result: Model = {
    type: "object",
    name: INTERNAL_RUN_MODEL_NAME,
    fields: nodes
      .filter((n) => isExtractionNode(n) || isDocumentNode(n))
      .reduce<Model[]>((acc, node, i) => {
        const key = node.model.name ?? `unnamed-${i}`;
        if (!key) {
          console.warn(`Found unnamed model: ${JSON.stringify(node, null, 2)}`);
        }

        const convertToList = isDocumentNode(node) && node.acceptMultiple;
        if (convertToList) {
          acc.push({
            type: "array",
            model: node.model,
            name: key,
          });
        } else {
          acc.push(node.model);
        }

        return acc;
      }, []),
  };

  return result;
};

export const createDefaultExportAction = (): ExportActionNode => ({
  id: crypto.randomUUID(),
  name: "Export",
  type: "action",
  subType: "export",
  dependencyIds: [],
  config: {
    format: "csv",
    fields: [],
  },
  runMode: "manual",
});

export const createDefaultExportExcelConfig = (
  templateDocumentId: string,
  sheetNames: string[],
): ExportExcelConfig => ({
  format: "excel",
  templateDocumentId,
  editTemplate: {
    sheets: sheetNames.map((sheetName) => ({
      name: sheetName,
      enabled: true,
      sheet: {
        cells: [],
        lists: [],
      },
    })),
  },
});

export const createDefaultRPAAction = (): RPAActionNode => ({
  id: crypto.randomUUID(),
  name: "RPA",
  type: "action",
  subType: "rpa",
  dependencyIds: [],
  nodes: [],
  edges: [],
  runMode: "manual",
});

export const createDefaultRESTAction = (): RESTActionNode => ({
  id: crypto.randomUUID(),
  name: "REST",
  type: "action",
  subType: "rest",
  dependencyIds: [],
  method: "GET",
  url: "",
  headers: [],
  body: "",
  runMode: "automatic",
});

export const createDefaultSendEmailAction = (): SendEmailActionNode => ({
  id: crypto.randomUUID(),
  name: "Send Email",
  type: "action",
  subType: "send-email",
  dependencyIds: [],
  config: {
    body: "",
  },
  runMode: "automatic",
});

export const enrichTemplatedText = (
  template: TemplatedText,
  data: ModelResult,
): string => {
  let obj: TiptapDoc | undefined;
  if (typeof template === "string") {
    try {
      obj = TiptapDocSchema.parse(JSON.parse(template));
    } catch {
      // If it's not a valid tiptap doc, just return the string
      // TODO: maybe also enrich text with embedded @* references
      return template;
    }
  } else {
    obj = template;
  }

  if (obj) {
    return enrichTiptapDoc(obj, data);
  }

  return "";
};

const enrichTiptapDoc = (doc: TiptapDoc, data: ModelResult): string => {
  let ret = "";
  const blocks: TiptapNode[] = [...doc.content];

  while (blocks.length > 0) {
    const block = blocks.shift();
    if (!block) continue;

    if (block.type === "paragraph") {
      blocks.push(...(block.content ?? []));
    } else if (block.type === "text") {
      ret += block.text;
    } else if (block.type === "mention" && block.attrs.id) {
      ret += _.get(data, block.attrs.id);
    }
  }

  return ret;
};
